import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ONBOARDED_REVENUE_REPORT, ONBOARDED_URL } from "../../constants/api";
import dayjs from "dayjs";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtCurrency = (v) =>
  v != null ? `₹${parseFloat(v).toLocaleString("en-IN")}` : "—";

const STATUS_COLORS = {
  "In Progress": "#3b82f6",
  "Go Live":     "#22c55e",
  "Completed":   "#10b981",
  "Suspended":   "#ef4444",
};

const DATE_PRESETS = [
  { value: "all",        label: "All Time" },
  { value: "today",      label: "Today" },
  { value: "last_week",  label: "Last 7 Days" },
  { value: "last_month", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "this_year",  label: "This Year" },
  { value: "custom",     label: "Custom Range" },
];

const resolveRange = (preset, customFrom, customTo) => {
  const today = dayjs();
  const fmt = d => d.format("YYYY-MM-DD");
  switch (preset) {
    case "today":      return { from_date: fmt(today),                         to_date: fmt(today) };
    case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),     to_date: fmt(today) };
    case "last_month": return { from_date: fmt(today.subtract(30, "day")),     to_date: fmt(today) };
    case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
    case "this_year":  return { from_date: fmt(today.startOf("year")),         to_date: fmt(today) };
    case "custom":     return (customFrom && customTo) ? { from_date: customFrom, to_date: customTo } : {};
    default:           return {};
  }
};

export default function OnboardedRevenueReport() {
  const { fetch }  = useApiMethods();
  const navigate   = useNavigate();

  const [summary,      setSummary]      = useState(null);
  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [datePreset,   setDatePreset]   = useState("all");
  const [customFrom,   setCustomFrom]   = useState("");
  const [customTo,     setCustomTo]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(25);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { from_date, to_date } = resolveRange(datePreset, customFrom, customTo);
      const params = new URLSearchParams();
      if (from_date)    params.append("from_date", from_date);
      if (to_date)      params.append("to_date",   to_date);
      if (statusFilter) params.append("status",    statusFilter);
      const qs = params.toString();

      const [rptRes, listRes] = await Promise.all([
        fetch(`${ONBOARDED_REVENUE_REPORT}${qs ? "?" + qs : ""}`),
        fetch(`${ONBOARDED_URL}${qs ? "?" + qs : ""}`),
      ]);
      if (rptRes?.status  === "success") setSummary(rptRes.data);
      if (listRes?.status === "success") setClients(listRes.data || []);
    } catch {
      ToastMsg("Failed to load revenue report", "error");
    } finally {
      setLoading(false);
    }
  }, [datePreset, customFrom, customTo, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setPage(1); }, [statusFilter, datePreset]);

  const totalPages = Math.max(1, Math.ceil(clients.length / pageSize));
  const paginated  = clients.slice((page - 1) * pageSize, page * pageSize);

  return (
    <ContainerCard>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Onboarded Revenue Report</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Contract value, AMC revenue, and renewal rates</p>
        </div>
        <Button onClick={loadData} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Summary pills */}
      {summary && (
        <div className="flex flex-wrap items-center gap-4 mb-4 px-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Total Clients: <span className="font-semibold text-gray-700 dark:text-gray-300">{summary.total_clients || 0}</span>
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Contract Value: <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtCurrency(summary.total_contract_value)}</span>
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            AMC Value: <span className="font-semibold text-gray-700 dark:text-gray-300">{fmtCurrency(summary.total_amc_value)}</span>
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            AMC Renewal Rate: <span className="font-semibold text-gray-700 dark:text-gray-300">{summary.renewal_rate_percent || 0}%</span>
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative">
          <select value={datePreset} onChange={e => { setDatePreset(e.target.value); if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); } }}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {datePreset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
            <Button onClick={() => loadData()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
          </>
        )}
        <div className="relative">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none cursor-pointer">
            <option value="">All Statuses</option>
            <option value="In Progress">In Progress</option>
            <option value="Go Live">Go Live</option>
            <option value="Completed">Completed</option>
            <option value="Suspended">Suspended</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
        {(datePreset !== "all" || statusFilter) && (
          <button onClick={() => { setDatePreset("all"); setStatusFilter(""); setCustomFrom(""); setCustomTo(""); }}
            className="text-xs text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300">Clear</button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Onboarded</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Client</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Product</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Payment Terms</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">Contract Value</th>
                <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-right">AMC Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {clients.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                    No clients found
                  </td>
                </tr>
              ) : (
                paginated.map((c, idx) => (
                  <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/onboarded/${c.id}`)}>
                    <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{fmtDate(c.onboarded_date)}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200">{c.org_name}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                        style={{ backgroundColor: (STATUS_COLORS[c.onboarding_status] || "#6b7280") + "20", color: STATUS_COLORS[c.onboarding_status] || "#6b7280" }}>
                        {c.onboarding_status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[120px] truncate">{c.product_sold || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400">{c.payment_terms || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-700 dark:text-gray-300">
                      {fmtCurrency(c.contract_value)}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-500 dark:text-gray-400">
                      {c.amc?.amc_value ? fmtCurrency(c.amc.amc_value) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination footer */}
          {clients.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none">
                  {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {clients.length === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, clients.length)}`} of {clients.length}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
                  .map((p, i) => p === "…"
                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                    : <button key={p} onClick={() => setPage(p)} className={`min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors ${p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>{p}</button>
                  )
                }
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
              </div>
            </div>
          )}
        </div>
      )}
    </ContainerCard>
  );
}
